import { api } from './api-client';

/** The tiers the API declares. Mirrors `ALL_TIERS` in api/src/llm/tiers.ts. */
export const LLM_TIERS = ['fast', 'standard'] as const;

export type LlmTier = (typeof LLM_TIERS)[number];

/**
 * What each tier is for, in the words an operator needs.
 *
 * Held here rather than fetched: it is editorial copy about a decision, not
 * data the server owns, and a round trip to be told what "fast" means would be
 * a round trip for nothing.
 */
export const TIER_BLURB: Record<LlmTier, string> = {
  fast: 'Work a learner is waiting on, and cheap classification. Lowest cost and lowest latency.',
  standard:
    'Work generated once and read many times — characters, scenes, the sentence the first run closes on.',
};

/** One tier's settings. Every field null means "use the default". */
export interface LlmOverrides {
  model: string | null;
  timeoutMs: number | null;
  maxAttempts: number | null;
  maxOutputTokens: number | null;
  thinkingBudget: number | null;
}

/** The same five settings, resolved. `model` is always present. */
export interface LlmResolved {
  model: string;
  timeoutMs: number;
  maxAttempts: number;
  maxOutputTokens: number | null;
  thinkingBudget: number | null;
}

export interface LlmTierSettings {
  tier: LlmTier;
  overrides: LlmOverrides;
  defaults: LlmResolved;
  /** What the next call will actually use. Computed by the API, not here. */
  effective: LlmResolved;
  updatedAt: string | null;
}

export interface LlmStatus {
  configured: boolean;
  tiers: { tier: LlmTier; model: string }[];
}

/** One model this deployment's key can reach. */
export interface LlmModel {
  id: string;
  displayName: string | null;
  description: string | null;
  inputTokenLimit: number | null;
  outputTokenLimit: number | null;
}

export interface LlmPingResult {
  text: string;
  call: {
    tier: LlmTier;
    model: string;
    latencyMs: number;
    promptTokens: number | null;
    outputTokens: number | null;
    thoughtTokens: number | null;
    totalTokens: number | null;
    finishReason: string | null;
  };
}

export async function getLlmStatus(): Promise<LlmStatus> {
  const { llm } = await api.get<{ llm: LlmStatus }>('/admin/llm');
  return llm;
}

export async function listLlmSettings(): Promise<LlmTierSettings[]> {
  const { settings } = await api.get<{ settings: LlmTierSettings[] }>(
    '/admin/llm/settings',
  );
  return settings;
}

export async function updateLlmSettings(
  tier: LlmTier,
  overrides: LlmOverrides,
): Promise<LlmTierSettings> {
  const { settings } = await api.patch<{ settings: LlmTierSettings }>(
    `/admin/llm/settings/${tier}`,
    overrides,
  );
  return settings;
}

export async function listLlmModels(): Promise<LlmModel[]> {
  const { models } = await api.get<{ models: LlmModel[] }>('/admin/llm/models');
  return models;
}

/** Spends real tokens. Only ever called from an explicit button press. */
export async function pingLlm(tier: LlmTier): Promise<LlmPingResult> {
  return api.post<LlmPingResult>('/admin/llm/ping', { tier });
}
